import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Save, X, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const Temperatures = () => {
  const [items, setItems] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', value_text: '', icon_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [itemsRes, iconsRes] = await Promise.all([api.get('/temperatures'), api.get('/icons')]);
      setItems(itemsRes.data);
      setIcons(iconsRes.data);
    } catch (error) {
      toast.error('Echec du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, icon_id: formData.icon_id || null };
      if (editingId) {
        await api.put(`/temperatures/${editingId}`, payload);
        toast.success('Temperature modifiee');
      } else {
        await api.post('/temperatures', payload);
        toast.success('Temperature creee');
      }
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, value_text: item.value_text || '', icon_id: item.icon_id || '' });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette temperature ?')) return;
    try {
      await api.delete(`/temperatures/${id}`);
      toast.success('Temperature supprimee');
      loadData();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', value_text: '', icon_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const getIconPreview = (iconId) => {
    const icon = icons.find((i) => i.id === iconId);
    if (!icon || !icon.image_url) return null;
    return <img src={BACKEND_BASE + icon.image_url} alt={icon.name} className="w-8 h-8 object-contain inline-block" />;
  };

  if (loading) return <Layout><div className="text-center py-12">Chargement...</div></Layout>;

  return (
    <Layout>
      <div data-testid="temperatures-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Temperatures
          </h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-temperature-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <Plus size={20} /><span>Ajouter</span>
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Temperature' : 'Nouvelle Temperature'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="temperature-form">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="temperature-name-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Ambiante, Eau chaude, Eau froide" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Valeur</label>
                  <input type="text" value={formData.value_text}
                    onChange={(e) => setFormData({ ...formData, value_text: e.target.value })}
                    data-testid="temperature-value-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: 20-25C, >60C, <5C" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Icone</label>
                  <select value={formData.icon_id}
                    onChange={(e) => setFormData({ ...formData, icon_id: e.target.value })}
                    data-testid="temperature-icon-select"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}>
                    <option value="">-- Aucune icone --</option>
                    {icons.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex space-x-3">
                <button type="submit" data-testid="save-temperature-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} /><span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} /><span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Icone</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Valeur</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                  Aucune temperature definie. Ajoutez vos premieres temperatures !
                </td></tr>
              ) : items.map((item) => (
                <tr key={item.id} data-testid={`temperature-row-${item.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                  <td className="px-6 py-4">{item.icon_id ? getIconPreview(item.icon_id) : <span style={{ color: '#9CA3AF' }}>-</span>}</td>
                  <td className="px-6 py-4 font-medium" style={{ color: '#0E2D43' }}>{item.name}</td>
                  <td className="px-6 py-4" style={{ color: '#6B7280' }}>{item.value_text || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleEdit(item)} data-testid={`edit-temperature-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded mr-2"
                      style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}><Edit size={16} /></button>
                    <button onClick={() => handleDelete(item.id)} data-testid={`delete-temperature-${item.id}`}
                      className="inline-flex items-center px-3 py-1 rounded"
                      style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Temperatures;
